// =============================================
// DASHBOARD MEJORADO CON GRÁFICAS
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TableSortLabel,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  AccountBalance as BalanceIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import statsService from '../../services/statsService';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ImprovedDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Estados para datos
  const [summary, setSummary] = useState(null);
  const [incomeExpenses, setIncomeExpenses] = useState([]);
  const [delinquencyTrend, setDelinquencyTrend] = useState([]);
  const [topDebtors, setTopDebtors] = useState([]);

  // Ordenamiento para Top Morosos
  const [topDebtorsOrderBy, setTopDebtorsOrderBy] = useState('totalDebt');
  const [topDebtorsOrder, setTopDebtorsOrder] = useState('desc');

  // Cargar todos los datos
  const loadAllData = async () => {
    try {
      setLoading(true);
      setError('');

      const [summaryRes, incomeExpensesRes, delinquencyRes, debtorsRes] =
        await Promise.all([
          statsService.getSummary(),
          statsService.getIncomeExpenses(),
          statsService.getDelinquencyTrend(),
          statsService.getTopDebtors(),
        ]);

      setSummary(summaryRes.stats);
      setIncomeExpenses(incomeExpensesRes.data);
      setDelinquencyTrend(delinquencyRes.data);
      setTopDebtors(debtorsRes.data);

    } catch (err) {
      setError('Error al cargar estadísticas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // Configuración de gráfica Ingresos vs Gastos
  const incomeExpensesChartData = {
    labels: incomeExpenses.map(d => d.month),
    datasets: [
      {
        label: 'Ingresos',
        data: incomeExpenses.map(d => d.income),
        backgroundColor: 'rgba(76, 175, 80, 0.7)',
        borderColor: 'rgba(76, 175, 80, 1)',
        borderWidth: 2,
      },
      {
        label: 'Gastos',
        data: incomeExpenses.map(d => d.expenses),
        backgroundColor: 'rgba(244, 67, 54, 0.7)',
        borderColor: 'rgba(244, 67, 54, 1)',
        borderWidth: 2,
      },
    ],
  };

  const incomeExpensesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Ingresos vs Gastos (Últimos 6 meses)',
        font: {
          size: 16,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return '$' + value;
          }
        }
      }
    }
  };

  // Configuración de gráfica de Morosidad
  const delinquencyChartData = {
    labels: delinquencyTrend.map(d => d.month),
    datasets: [
      {
        label: 'Morosidad (%)',
        data: delinquencyTrend.map(d => d.rate),
        fill: true,
        backgroundColor: 'rgba(255, 152, 0, 0.2)',
        borderColor: 'rgba(255, 152, 0, 1)',
        borderWidth: 2,
        tension: 0.4,
      },
    ],
  };

  const delinquencyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Evolución de Morosidad (Últimos 12 meses)',
        font: {
          size: 16,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `Morosidad: ${context.parsed.y}%`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function (value) {
            return value + '%';
          }
        }
      }
    }
  };

  // Manejar ordenamiento de Top Morosos
  const handleTopDebtorsRequestSort = (property) => {
    const isAsc = topDebtorsOrderBy === property && topDebtorsOrder === 'asc';
    setTopDebtorsOrder(isAsc ? 'desc' : 'asc');
    setTopDebtorsOrderBy(property);
  };

  const topDebtorsDescendingComparator = (a, b, orderBy) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';

    if (orderBy === 'houseNumber') {
      const aNum = parseInt(aVal);
      const bNum = parseInt(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        aVal = aNum;
        bVal = bNum;
      }
    }

    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  };

  const getTopDebtorsComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => topDebtorsDescendingComparator(a, b, orderBy)
      : (a, b) => -topDebtorsDescendingComparator(a, b, orderBy);
  };


  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando estadísticas...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Título */}
      <Typography variant="h4" gutterBottom>
        📊 Dashboard Ejecutivo
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* KPIs - Tarjetas de Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Ingresos */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MoneyIcon sx={{ color: '#4caf50', mr: 1 }} />
                <Typography color="text.secondary" variant="body2">
                  Ingresos del Mes
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                ${summary?.income.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Esperado: ${summary?.expectedIncome.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Gastos */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingDownIcon sx={{ color: '#f44336', mr: 1 }} />
                <Typography color="text.secondary" variant="body2">
                  Gastos del Mes
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                ${summary?.expenses.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Balance */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: summary?.balance >= 0 ? '#e3f2fd' : '#fff3e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BalanceIcon sx={{ color: summary?.balance >= 0 ? '#1976d2' : '#ff9800', mr: 1 }} />
                <Typography color="text.secondary" variant="body2">
                  Balance del Mes
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  color: summary?.balance >= 0 ? '#1565c0' : '#ef6c00',
                  fontWeight: 'bold'
                }}
              >
                ${summary?.balance.toFixed(2)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                {summary?.balance >= 0 ? (
                  <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                ) : (
                  <TrendingDownIcon sx={{ color: '#f44336', fontSize: 18 }} />
                )}
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                  {summary?.balance >= 0 ? 'Positivo' : 'Negativo'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Morosidad */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WarningIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Typography color="text.secondary" variant="body2">
                  Morosidad
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#ef6c00', fontWeight: 'bold' }}>
                {summary?.delinquencyRate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pendiente: ${summary?.pendingAmount.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Ingresos vs Gastos */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Bar data={incomeExpensesChartData} options={incomeExpensesOptions} />
          </Paper>
        </Grid>

        {/* Evolución de Morosidad */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Line data={delinquencyChartData} options={delinquencyOptions} />
          </Paper>
        </Grid>
      </Grid>

      {/* Top 5 Morosos */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          🔴 Top 5 Morosos
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={topDebtorsOrderBy === 'name'}
                    direction={topDebtorsOrderBy === 'name' ? topDebtorsOrder : 'asc'}
                    onClick={() => handleTopDebtorsRequestSort('name')}
                  >
                    <strong>Propietario</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={topDebtorsOrderBy === 'houseNumber'}
                    direction={topDebtorsOrderBy === 'houseNumber' ? topDebtorsOrder : 'asc'}
                    onClick={() => handleTopDebtorsRequestSort('houseNumber')}
                  >
                    <strong>Casa</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={topDebtorsOrderBy === 'totalDebt'}
                    direction={topDebtorsOrderBy === 'totalDebt' ? topDebtorsOrder : 'asc'}
                    onClick={() => handleTopDebtorsRequestSort('totalDebt')}
                  >
                    <strong>Deuda Total</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={topDebtorsOrderBy === 'monthsPending'}
                    direction={topDebtorsOrderBy === 'monthsPending' ? topDebtorsOrder : 'asc'}
                    onClick={() => handleTopDebtorsRequestSort('monthsPending')}
                  >
                    <strong>Meses Pendientes</strong>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topDebtors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      ¡No hay morosos! 🎉
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                [...topDebtors].sort(getTopDebtorsComparator(topDebtorsOrder, topDebtorsOrderBy)).map((debtor, index) => (
                  <TableRow key={debtor.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          label={index + 1}
                          size="small"
                          color={index === 0 ? 'error' : 'default'}
                          sx={{ mr: 1, minWidth: 30 }}
                        />
                        {debtor.name}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={`#${debtor.houseNumber}`} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ color: '#d32f2f', fontWeight: 'bold' }}
                      >
                        ${debtor.totalDebt.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={debtor.monthsPending}
                        size="small"
                        color="warning"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default ImprovedDashboard;
